/**
 * Project: Aniatek
 * Author: Carlos A. Loaiza | Connect & Launch
 * Tel: +52 (667) 247 - 6316
 * Intellectual Property: All rights reserved (c) 2026
 */

<template>
  <div class="paso-tres-container">
    <div class="form-section-header">
      <h3 class="header-icon">Paso 3: Equipo</h3>
      <p>Configura los paneles solares, inversores y otros componentes del sistema.</p>
    </div>

    <div class="autoconsumo-bar">
      <div class="autoconsumo-slider-group">
        <label>Porcentaje de autoconsumo</label>
      </div>
      <div class="autoconsumo-result">
       <strong>{{ porcentajeCobertura }}%</strong>

        <div class="meta-info">
          <span>Potencia a instalar: <strong>{{ potenciaSistemaKWp }} kWp </strong></span>
          <span class="stat-item highlight">
            Para 100%: <strong>{{ potenciaPara100.toFixed(2) }} kWp</strong>
          </span>
        </div>
      </div>
    </div>

    <div class="demanda-bar">
      <div class="solar-info-bar">
        <div class="info-item">
          <span class="label">Ubicación del Proyecto:</span>
          <span class="value">{{ formData.localidadProyecto || 'No definida' }}</span>
        </div>

        <div class="info-item">
          <span class="label">Irradiancia Solar (HSP):</span>
          <span class="value" v-if="loadingHsp">Cargando datos NASA...</span>
          <span class="value highlight" v-else>
            {{ hspPromedio.toFixed(2) }} kWh/m²/día
            <small v-if="hspPromedio === 5.0">(Promedio MX)</small>
          </span>
          <div class="info-item" style="border-left: 1px solid #ccc; padding-left: 15px; margin-left: 15px;">
            <span class="label">Eficiencia del Sistema (%):</span>
            <input type="number" min="1" max="100"
              style="width: 60px; padding: 2px 5px; border-radius: 4px; border: 1px solid #ccc;"
              :value="formData.eficiencia || 75" @input="updateField('eficiencia', $event.target.value)">
          </div>
        </div>
      </div>
    </div>

    <div class="equipment-section">
      <h4 class="equipment-section-title">Paneles Solares</h4>
      <div class="equipment-grid">

        <div class="form-group span-2">
          <label>Modelo del panel</label>
          <select :value="formData.idPanel" @change="updateField('idPanel', $event.target.value)">
            <option value="">-- Selecciona un Panel --</option>
            <template v-if="listas && listas.paneles">
              <option v-for="panel in listas.paneles" :key="panel.id_panel" :value="panel.id_panel">
                {{ panel.Modelo }} - {{ panel.Potencia }}W
              </option>
            </template>
          </select>
        </div>

        <div class="form-group">
          <label>Cantidad</label>
          <input type="number" min="1" :value="formData.cantidadPaneles"
            @input="updateField('cantidadPaneles', $event.target.value)">
        </div>

        <div class="form-group">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
            <label style="margin-bottom: 0;">Potencia Total</label>
          </div>
          <input type="text" class="readonly-input" :value="potenciaSistemaKWp + ' kWp'" readonly>
        </div>

        <div class="form-group">
          <label>Producción Est.</label>
          <input type="text" class="readonly-input" :value="produccionEstimada + ' kWh/mes'" readonly>
        </div>

      </div>
    </div>

    <div class="equipment-section">
      <h4 class="equipment-section-title">Inversores</h4>
      <div class="equipment-grid">

        <div class="form-group span-2">
          <label>Modelo</label>
          <select :value="formData.idInversor" @change="updateField('idInversor', $event.target.value)">
            <option value="">-- Selecciona un Inversor --</option>
            <template v-if="listas && listas.inversores">
              <option v-for="inv in listas.inversores" :key="inv.id_inversor" :value="inv.id_inversor">
                {{ inv.Modelo }}
              </option>
            </template>
          </select>
        </div>

        <div class="form-group">
          <label>Cantidad</label>
          <input type="number" min="1" :value="formData.cantidadInversores"
            @input="updateField('cantidadInversores', $event.target.value)">
        </div>

        <div class="form-group">
          <label>Potencia (W)</label>
          <input type="text" class="readonly-input" :value="datosInversor.potencia + ' W'" disabled>
        </div>

        <div class="form-group">
          <label>Voltaje Máx.</label>
          <input type="text" class="readonly-input" :value="datosInversor.voltaje + ' V'" disabled>
        </div>

        <div class="form-group">
          <label>Corriente Máx.</label>
          <input type="text" class="readonly-input" :value="datosInversor.corriente + ' A'" disabled>
        </div>

      </div>
    </div>

  </div>
</template>

<script>
import nasaService from '@/services/nasaService';

export default {
  name: 'PasoTres',
  props: {
    formData: { type: Object, required: true },
    listas: { type: Object, default: () => ({ paneles: [], inversores: [] }) },
    averageKwh: { type: Number, default: 0 },
    limiteKwp: { type: Number, default: 10 }
  },
  data() {
    return {
      manualOverride: false,
      hspPromedio: 5.0,
      loadingHsp: false,
      hspDataMensual: null,
    };
  },
  computed: {
    factorEficiencia() {
      const val = parseFloat(this.formData.eficiencia);
      if (!val || val <= 0) return 0.75; 
      return val / 100;
    },
    potenciaPara100() {
      if (!this.averageKwh || this.averageKwh <= 0) return 0;
      const hsp = this.hspPromedio || 5.0;
      return this.averageKwh / (hsp * 30.4 * this.factorEficiencia);
    },
    inversorSeleccionado() {
      if (!this.listas?.inversores || !this.formData.idInversor) return null;
      return this.listas.inversores.find(i => i.id_inversor == this.formData.idInversor);
    },
    panelSeleccionado() {
      if (!this.listas?.paneles || !this.formData.idPanel) return null;
      return this.listas.paneles.find(p => p.id_panel == this.formData.idPanel);
    },
    potenciaSistemaKWp() {
      if (!this.panelSeleccionado || !this.formData.cantidadPaneles) return "0.00";
      return ((this.panelSeleccionado.Pmax * this.formData.cantidadPaneles) / 1000).toFixed(2);
    },
    produccionEstimada() {
      const kwp = parseFloat(this.potenciaSistemaKWp);
      if (kwp === 0) return "0.00";
      const produccion = kwp * this.hspPromedio * 30.4 * this.factorEficiencia;
      return produccion.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    panelesIdeales100() {
      if (!this.panelSeleccionado || this.potenciaPara100 <= 0) return 0;
      const potenciaPanelKW = this.panelSeleccionado.Pmax / 1000;
      return Math.ceil(this.potenciaPara100 / potenciaPanelKW);
    },

    porcentajeCobertura() {
      if (this.panelesIdeales100 === 0 || !this.formData.cantidadPaneles) return 0;
      let porcentaje = (this.formData.cantidadPaneles / this.panelesIdeales100) * 100;
      
      return Math.round(porcentaje);
    },
    maxPanelesPermitidos() {
      if (!this.panelSeleccionado || !this.limiteKwp) return 0;
      return Math.floor((this.limiteKwp * 1000) / this.panelSeleccionado.Pmax);
    },
    datosInversor() {
      const inv = this.inversorSeleccionado;
      if (!inv) return { potencia: 0, corriente: 0, voltaje: 0 };
      return {
        potencia: inv.Potencia || 0,
        corriente: inv.corriente || 0,
        voltaje: inv.voltaje || 0
      };
    }
  },
  watch: {
    'formData.idPanel': function (newVal, oldVal) {
      if (!newVal || newVal === oldVal) return;
      this.manualOverride = false;
      this.$nextTick(() => { this.calcularPanelesSugeridos(); });
    },
    'formData.idInversor': function (newVal) {
      if (!newVal) return;
      this.sugerirInversores();
    },
    'formData.eficiencia': function () {
      if (this.manualOverride) return;
      this.calcularPanelesSugeridos();
    },
    limiteKwp: function () {
      if (this.formData.limitarProyecto) this.calcularPanelesSugeridos();
    },
    'formData.limitarProyecto': function () {
      this.manualOverride = false;
      this.calcularPanelesSugeridos();
    }
  },
  methods: {
    updateField(field, value) {
      let val = value;
      if (['cantidadPaneles', 'cantidadInversores', 'idPanel', 'idInversor', 'porcentajeAutoconsumo', 'eficiencia'].includes(field)) {
        val = Number(value) || 0; 
      }

      let nuevosDatos = { ...this.formData, [field]: val };
      this.$emit('update:formData', nuevosDatos);

      if (field === 'cantidadPaneles') {
        this.manualOverride = true;
        this.$nextTick(() => { this.sugerirInversores(val); });
      }

      if (field === 'eficiencia') {
        this.manualOverride = false;
        this.$nextTick(() => { this.calcularPanelesSugeridos(); });
      }
    },

    async obtenerDatosSolares() {
      const lat = this.formData.latitud;
      const lon = this.formData.longitud;
      if (!lat || !lon) return;

      this.loadingHsp = true;
      const data = await nasaService.getHSP(lat, lon);

      if (data && data.ANN) {
        this.hspPromedio = data.ANN;
        this.hspDataMensual = data;

        this.$emit('update:formData', { ...this.formData, hspGuardado: this.hspPromedio });

        if (!this.manualOverride) {
          this.calcularPanelesSugeridos();
        }
      }
      this.loadingHsp = false;
    },

    calcularPanelesSugeridos() {
      if (this.manualOverride) return;
      if (!this.panelSeleccionado || !this.averageKwh || this.averageKwh <= 0) return;

      const hsp = this.hspPromedio || 5.0;
      const porcentaje = (this.formData.porcentajeAutoconsumo || 100) / 100;

      const kwpNecesarios = (this.averageKwh * porcentaje) / (hsp * 30.4 * this.factorEficiencia);

      const potenciaPanelKW = this.panelSeleccionado.Pmax / 1000;

      let panelesCalculados = Math.ceil(kwpNecesarios / potenciaPanelKW);
      if (panelesCalculados < 1) panelesCalculados = 1;

      if (this.formData.limitarProyecto) {
        panelesCalculados = Math.min(panelesCalculados, this.maxPanelesPermitidos);
      }

      this.$emit('update:formData', {
        ...this.formData,
        cantidadPaneles: panelesCalculados
      });

      this.$nextTick(() => { this.sugerirInversores(panelesCalculados); });
    },

    sugerirInversores(cantidadPanelesManual = null) {
      if (!this.inversorSeleccionado || !this.panelSeleccionado) return;

      const cantidadPaneles = cantidadPanelesManual !== null ? cantidadPanelesManual : this.formData.cantidadPaneles;
      if (cantidadPaneles <= 0) return;

      const potenciaTotalW = cantidadPaneles * this.panelSeleccionado.Pmax;
      const potenciaInversorW = this.inversorSeleccionado.Potencia || this.inversorSeleccionado.Pmax || 0;

      if (potenciaInversorW === 0) return;

      const capacidadRealSoportada = potenciaInversorW * 1.30; 
      let cantidadNecesaria = Math.ceil(potenciaTotalW / capacidadRealSoportada);
      if (cantidadNecesaria < 1) cantidadNecesaria = 1;

      this.$emit('update:formData', {
        ...this.formData,
        cantidadInversores: cantidadNecesaria
      });
    }
  },
  mounted() {
    this.obtenerDatosSolares();

    if (this.formData.cantidadPaneles > 0) {
      this.manualOverride = true;
    } else {
      this.calcularPanelesSugeridos();
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  },
}
</script>

<style scoped>
.form-section-header {
  margin-bottom: 30px;
}

.form-section-header h3 {
  font-size: 20px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 8px;
}

.header-icon {
  display: flex;
  align-items: center;
  gap: 10px;
}

.header-icon svg {
  color: var(--color-primario);
  width: 20px;
  height: 20px;
}

.form-section-header p {
  font-size: 15px;
  color: var(--color-texto-secundario);
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  margin-bottom: 8px;
  font-size: 14px;
  color: var(--color-texto-secundario);
  font-weight: 600;
}

.form-group input,
.form-group select {
  height: 42px;
  border: 1px solid var(--color-borde);
  border-radius: 8px;
  padding: 0 15px;
  font-size: 14px;
  background-color: var(--color-fondo-blanco);
  transition: border-color 0.3s, box-shadow 0.3s;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: var(--color-primario);
  box-shadow: 0 0 0 3px rgba(47, 141, 183, 0.2);
}

.readonly-input {
  background-color: var(--color-fondo-claro) !important;
  color: var(--color-texto-secundario);
  cursor: default;
  box-shadow: none !important;
}

.autoconsumo-bar,
.demanda-bar {
  display: flex;
  align-items: center;
  gap: 30px;
  background-color: var(--color-fondo-claro);
  padding: 20px;
  border-radius: 12px;
  margin-bottom: 25px;
  border: 1px solid var(--color-borde);
}

.autoconsumo-slider-group {
  flex-grow: 1;
}

.autoconsumo-slider-group label {
  font-weight: 600;
  color: var(--color-texto-principal);
}

.autoconsumo-result {
  background: var(--color-primario);
  color: white;
  padding: 10px 20px;
  border-radius: 8px;
  text-align: center;
  flex-shrink: 0;
  width: 80%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.autoconsumo-result strong {
  font-size: 24px;
}

.meta-info {
  margin-top: 5px;
  font-size: 14px;
  display: flex;
  gap: 15px;
}

.solar-info-bar {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-item .label {
  font-weight: 600;
  color: #4a5568;
}

.info-item .value {
  font-weight: bold;
  color: #2d3748;
}

.info-item .highlight {
  color: #29b9f6;
}

.equipment-section {
  border: 1px solid var(--color-borde);
  border-radius: 12px;
  padding: 25px;
  margin-top: 25px;
}

.equipment-section-title {
  font-size: 16px;
  font-weight: 600;
  color: #29b9f6;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e2e8f0;
  margin-top: 0px;
}

.equipment-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 20px;
}

.span-2 {
  grid-column: span 2;
}

@media (max-width: 768px) {

  .autoconsumo-bar,
  .demanda-bar {
    flex-direction: column;
    align-items: stretch;
    gap: 15px;
    padding: 15px;
  }

  .solar-info-bar {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .meta-info {
    flex-direction: column;
    gap: 5px;
  }

  .equipment-grid {
    display: flex;
    flex-direction: column;
    gap: 15px;
  }

  .equipment-section {
    padding: 15px;
  }
}
</style>